import { Component, DestroyRef, Injectable, OnInit } from '@angular/core';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { checkPanelCount, remainingDoorNumber } from 'src/app/services/helpers/fence-helper';
import { FenceConfiguratorItem } from '../../configurator/applications/fence-configurator-item';
import { IBaseComponent } from '../../configurator/i-base-component';
import { FencePreviewComponent } from '../preview/preview.component';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { MaterialsFacadeService } from './services/materials.facade.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubStepEnum } from './models/sub-step.enum';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { ColorStateService } from '../services/color.state.service';
import { environment } from 'src/environments/environment';
import { MaterialSubStepService } from './services/material-sub-step.service';

@Component({
  selector: 'ng-fence-materials',
  templateUrl: './materials.component.html',
  providers: [MaterialSubStepService, MaterialsFacadeService]
})
@Injectable({ providedIn: 'root' })
export class FenceMaterialsComponent extends FenceConfiguratorItem implements OnInit, IBaseComponent {
  /** FencePreview */
  fencePreview: FencePreviewComponent;
  protected readonly SubStepEnum = SubStepEnum;

  isVendor!: boolean;

  constructor(
    fenceService: FenceService,
    configurators2dComponent: Configurators2DComponent,
    private materialsFacadeService: MaterialsFacadeService,
    private colorStateService: ColorStateService,
    private destroyRef: DestroyRef
  ) {
    super(fenceService);
    this.fencePreview = configurators2dComponent.preview as FencePreviewComponent;
    this.isVendor = environment.vendor;
  }

  /**
   * Check si une couleur est sélectionnée et n'est pas désactivée
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkColorSelectionContainDisabled(): boolean {
    if (this.isVendor) {
      return false;
    }

    for (const configurationInstallation of this.configuration.configurationInstallations) {
      const colorIds = configurationInstallation.configurationBoards.map(b => b.boardId);

      const colors = this.colorStateService
        .colorsByInstallations()
        .get(configurationInstallation.installationId)
        .filter(c => colorIds.includes(c.id));

      if (colors.some(c => c.isDisabled)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check si un poteau est sélectionné et n'est pas désactivé
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkPostTypeSelectionIsCorrect(): boolean {
    return this.configuration.postType && (this.isVendor || !this.configuration.postType.isDisabled);
  }

  /**
   * Check si tous les portillons sélectionnés ne sont pas désactivés et que tous les emplacements de portillon sont pris.
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkDoorsSelectionIsCorrect(): boolean {
    return remainingDoorNumber(this.configuration) === 0 && (this.isVendor || this.configuration.doors?.every(d => !d.isDisabled));
  }

  /**
   * Initialisation du composant
   * Configure la langue courante et initialise le service facade des matériaux
   */
  ngOnInit(): void {
    this.materialsFacadeService
      .init(this.configuration, this.fencePreview)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur init étape matériaux et décors:', err),
      });
  }

  /**
   * Détermine si le bouton "Suivant" doit être activé
   * Vérifie que tous les éléments obligatoires sont sélectionnés et valides :
   * - Lame, couleur et type de poteau sélectionnés
   * - Toutes les lames décoratives/décorations/panneaux ont une quantité définie
   * - Le nombre de portillons correspond aux besoins
   * - Le nombre de panneaux ne dépasse pas le maximum autorisé
   * @returns true si le bouton peut être activé, false sinon
   */
  isNextButtonActive(): boolean {
    const checkDecorativeElementsCompleted = this.materialsFacadeService.checkDecorativeElementsCompleted(this.configuration);

    return (
      this.configuration.configurationInstallations.length > 0 &&
      this.configuration.configurationInstallations.every(i => i.configurationBoards.length > 0) &&
      !this.checkColorSelectionContainDisabled &&
      this.checkPostTypeSelectionIsCorrect &&
      checkDecorativeElementsCompleted &&
      this.checkDoorsSelectionIsCorrect &&
      checkPanelCount(this.configuration)
    );
  }

  /**
   * Vérifie que la configuration est complète avant de permettre le changement de page
   * Contrôle que tous les côtés ont au moins une section et que toutes les sections
   * ont une longueur et une hauteur définies
   * @returns true si le changement de page est autorisé, false sinon
   */
  checkPageChangeCapability(): boolean {
    let isSideUncompleted = false;
    let isSectionMissing = false;
    if (this.configuration.sides !== undefined) {
      for (let i = 0; i < this.configuration.sides.length; i++) {
        if (this.configuration.sides[i].sections.length === 0) {
          isSectionMissing = true;
        } else {
          for (let j = 0; j < this.configuration.sides[i].sections.length; j++) {
            if (
              this.configuration.sides[i].sections[j].length === 0 ||
              this.configuration.sides[i].sections[j].height === 0
            ) {
              isSideUncompleted = true;
            }
          }
        }
      }
    }

    return this.configuration.shape !== undefined && !isSideUncompleted && !isSectionMissing;
  }
}
